import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ShoppingBag } from 'lucide-react'
import { Button } from '@/components/ui/button'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Dialog, DialogDrawerContent } from '@/components/ui/dialog'
import { useToteStore } from '@/stores/tote'
import { useSession } from '@/hooks/use-session'
import { useLocaleNavigate } from '@/hooks/use-locale-navigate'
import { ToteItemRow } from './ToteItemRow'

export function ToteSheet() {
  const { t } = useTranslation('common')
  const { items, isOpen, closeTote } = useToteStore()
  const { isAuthenticated } = useSession()
  const navigate = useNavigate()
  const { navigate: localeNavigate } = useLocaleNavigate()

  // The tote is opened from many places (chrome icon, offer cards, etc.) via
  // Zustand state, not Radix's own <Dialog.Trigger> — so Radix has no idea
  // which element to return focus to on close and its built-in attempt is a
  // silent no-op. Capture whatever was actually focused right before opening
  // and restore it ourselves via onCloseAutoFocus below.
  const previouslyFocusedRef = useRef<HTMLElement | null>(null)
  useEffect(() => {
    if (isOpen) {
      previouslyFocusedRef.current = document.activeElement as HTMLElement | null
    }
  }, [isOpen])

  const total = items.reduce((sum, i) => sum + i.drops, 0)

  function handleCheckout() {
    if (!isAuthenticated) {
      closeTote()
      navigate('/sign-in')
      return
    }
    if (items.length === 0) return
    closeTote()
    const offerIds = items.map((i) => i.offerId).join(',')
    localeNavigate(`/claim/${items[0].offerId}?offerIds=${offerIds}`)
  }

  function handleOpenChange(open: boolean) {
    if (!open) {
      closeTote()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogDrawerContent
        onCloseAutoFocus={(e) => {
          e.preventDefault()
          previouslyFocusedRef.current?.focus()
        }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex-shrink-0">
          <DialogPrimitive.Title asChild>
            <h2 className="text-base font-semibold text-slate-800 dark:text-sand-100">
              {t('tote.title')}
              {items.length > 0 && (
                <span className="ml-2 text-sm font-normal text-slate-500 dark:text-slate-400">
                  {t('tote.itemCount', { count: items.length })}
                </span>
              )}
            </h2>
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            {t('tote.title')}
          </DialogPrimitive.Description>
          <button
            onClick={closeTote}
            className="rounded-lg w-11 h-11 flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            aria-label={t('tote.closeAriaLabel')}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-4 h-4">
              <path d="M12 4L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-center py-16 px-6">
              <ShoppingBag className="h-9 w-9 text-slate-300 dark:text-slate-600" strokeWidth={1.5} />
              <div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-300">{t('tote.empty')}</p>
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{t('tote.emptyHint')}</p>
              </div>
            </div>
          ) : (
            <div className="px-5">
              {items.map((item) => <ToteItemRow key={item.offerId} item={item} />)}
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="px-5 py-4 border-t border-slate-200 dark:border-slate-800 space-y-3 flex-shrink-0">
            <div className="flex justify-between items-baseline">
              <span className="text-sm text-slate-500 dark:text-slate-400">{t('tote.total')}</span>
              <span className="text-base font-semibold text-slate-800 dark:text-sand-100 tabular-nums">
                {total.toLocaleString()} {t('tote.drops')}
              </span>
            </div>
            <Button size="lg" className="w-full bg-ocean-500 hover:bg-ocean-600 dark:bg-ocean-400 dark:hover:bg-ocean-500 text-white font-display text-base shadow-md" onClick={handleCheckout}>
              {t('tote.checkout')}
            </Button>
          </div>
        )}
      </DialogDrawerContent>
    </Dialog>
  )
}
